import React from 'react';
import {Modal} from 'antd';
import {ArrowDownOutlined, ArrowUpOutlined, SafetyCertificateOutlined, WarningOutlined} from '@ant-design/icons';
import './TradingConfirmDialog.css';

export interface TradingConfirmDialogProps {
    direction: 'long' | 'short';
    instId: string;
    orderType: 'market' | 'limit';
    limitPrice?: number;
    currentPrice?: number;
    allocationPercent: number;
    lever: number;
    estimatedMargin?: number;
    estimatedQuantity?: number;
    notional?: number;
    takeProfitPrice?: number;
    stopLossPrice?: number;
    isLiveTrading: boolean;
    onConfirm: () => void | Promise<void>;
}

function formatPrice(price: number | undefined, fallback: string = '--'): string {
    if (!price || !Number.isFinite(price) || price <= 0) return fallback;
    return `$${price.toFixed(4)}`;
}

function formatQuantity(quantity: number | undefined): string {
    if (null == quantity || !Number.isFinite(quantity)) return '--';
    return quantity.toFixed(2);
}

function formatPercent(value: number): string {
    if (!Number.isFinite(value)) return '--';
    return `${Number(value.toFixed(2))}%`;
}

function relativePercent(price: number | undefined, orderPrice: number | undefined): string | null {
    if (!price || !orderPrice || !Number.isFinite(price) || !Number.isFinite(orderPrice) || orderPrice <= 0) {
        return null;
    }
    const percent = (price - orderPrice) / orderPrice * 100;
    return `${percent > 0 ? '+' : ''}${percent.toFixed(2)}%`;
}

export function showTradingConfirmDialog(props: TradingConfirmDialogProps): void {
    const {
        direction,
        instId,
        orderType,
        limitPrice,
        currentPrice,
        allocationPercent,
        lever,
        estimatedMargin,
        estimatedQuantity,
        notional,
        takeProfitPrice,
        stopLossPrice,
        isLiveTrading,
        onConfirm,
    } = props;

    const isLong = direction === 'long';
    const orderPrice = orderType === 'limit' && limitPrice ? limitPrice : currentPrice;
    const takeProfitPercent = relativePercent(takeProfitPrice, orderPrice);
    const stopLossPercent = relativePercent(stopLossPrice, orderPrice);
    const DirectionIcon = isLong ? ArrowUpOutlined : ArrowDownOutlined;

    Modal.confirm({
        title: null,
        icon: null,
        width: 520,
        centered: true,
        maskClosable: false,
        className: `pattern-trade-confirm-modal ${isLong ? 'is-long' : 'is-short'}`,
        content: (
            <div className="pt-confirm">
                <header className="pt-confirm-header">
                    <div className="pt-confirm-heading">
                        <span className="pt-confirm-kicker">ORDER PREVIEW</span>
                        <div className="pt-confirm-title-row">
                            <span className="pt-confirm-direction-icon"><DirectionIcon/></span>
                            <h2>{isLong ? '确认开多' : '确认开空'}</h2>
                            <span className="pt-confirm-symbol">{instId}</span>
                        </div>
                    </div>
                    <span className={`pt-confirm-mode ${isLiveTrading ? 'live' : 'paper'}`}>
                        <i/>{isLiveTrading ? '实盘' : '模拟盘'}
                    </span>
                </header>

                {isLiveTrading ? (
                    <div className="pt-confirm-live-warning">
                        <WarningOutlined/>
                        <span>实盘订单将使用真实资金，请确认价格与方向。</span>
                    </div>
                ) : null}

                <section className="pt-confirm-summary" aria-label="订单摘要">
                    <div className="pt-confirm-summary-item primary">
                        <span>预估保证金</span>
                        <strong>
                            {null != estimatedMargin && Number.isFinite(estimatedMargin)
                                ? `$${estimatedMargin.toFixed(2)}` : '--'}
                        </strong>
                        <small>USDT</small>
                    </div>
                    <div className="pt-confirm-summary-item">
                        <span>预估数量</span>
                        <strong>{formatQuantity(estimatedQuantity)}</strong>
                        <small>CONTRACTS</small>
                    </div>
                    <div className="pt-confirm-summary-item">
                        <span>名义价值</span>
                        <strong>{null != notional && Number.isFinite(notional) ? `$${notional.toFixed(2)}` : '--'}</strong>
                        <small>USDT</small>
                    </div>
                </section>

                <section className="pt-confirm-details" aria-label="交易参数">
                    <div className="pt-confirm-detail">
                        <span>订单类型</span>
                        <strong>{orderType === 'market' ? '市价单' : '限价单'}</strong>
                    </div>
                    <div className="pt-confirm-detail">
                        <span>委托价格</span>
                        <strong>{formatPrice(orderPrice)}</strong>
                    </div>
                    <div className="pt-confirm-detail">
                        <span>开仓比例</span>
                        <strong>{formatPercent(allocationPercent)}</strong>
                    </div>
                    <div className="pt-confirm-detail">
                        <span>杠杆倍数</span>
                        <strong>{lever}x</strong>
                    </div>
                </section>

                <section className="pt-confirm-protection" aria-label="止盈止损">
                    <div className="pt-confirm-protection-item take-profit">
                        <span>止盈</span>
                        <strong>{formatPrice(takeProfitPrice)}</strong>
                        {takeProfitPercent ? <small>{takeProfitPercent}</small> : null}
                    </div>
                    <div className="pt-confirm-protection-divider"/>
                    <div className="pt-confirm-protection-item stop-loss">
                        <span>止损</span>
                        <strong>{formatPrice(stopLossPrice)}</strong>
                        {stopLossPercent ? <small>{stopLossPercent}</small> : null}
                    </div>
                </section>

                <footer className="pt-confirm-note">
                    <SafetyCertificateOutlined/>
                    <span>确认后将立即提交订单，请再次核对交易参数</span>
                </footer>
            </div>
        ),
        okText: isLong ? '确认开多' : '确认开空',
        cancelText: '取消',
        okButtonProps: {
            type: 'primary',
            size: 'large',
            className: 'pt-confirm-submit',
        },
        cancelButtonProps: {
            size: 'large',
            className: 'pt-confirm-cancel',
        },
        onOk: onConfirm,
    });
}
